import { format, isValid, parseISO } from 'date-fns';

type ChartDataPoint = { [key: string]: string | number };

interface SmartDateFormatterOptions {
  sameYearFormat?: string;
}

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}(?:[T\s].*)?$/;

export const parseDateValue = (value: string | number | undefined): Date | null => {
  if (value === undefined || value === null || typeof value === 'number') {
    return null;
  }

  if (!ISO_DATE_PATTERN.test(value)) {
    return null;
  }

  const parsedDate = parseISO(value);
  return isValid(parsedDate) ? parsedDate : null;
};

export const createSmartDateFormatter = (
  data: ChartDataPoint[],
  dataKey?: string,
  fallbackFormatter?: (value: string) => string,
  options: SmartDateFormatterOptions = {}
): ((value: string | number) => string) => {
  if (!dataKey) {
    return (value: string | number): string =>
      fallbackFormatter ? fallbackFormatter(String(value)) : String(value);
  }

  const values = data
    .map((item: ChartDataPoint) => item[dataKey])
    .filter((value: string | number | undefined): value is string | number => value !== undefined);
  const dates = data
    .map((item: ChartDataPoint) => parseDateValue(item[dataKey]))
    .filter((date: Date | null): date is Date => date !== null);

  if (values.length === 0 || dates.length !== values.length) {
    return (value: string | number): string =>
      fallbackFormatter ? fallbackFormatter(String(value)) : String(value);
  }

  const allDatesAreSameDay =
    dates.length > 0 &&
    dates.every((date: Date) => format(date, 'yyyy-MM-dd') === format(dates[0], 'yyyy-MM-dd'));
  const allDatesAreSameYear =
    dates.length > 0 && dates.every((date: Date) => date.getFullYear() === dates[0].getFullYear());

  return (value: string | number): string => {
    const date = parseDateValue(value);

    if (!date) {
      return fallbackFormatter ? fallbackFormatter(String(value)) : String(value);
    }

    if (allDatesAreSameDay) {
      return format(date, 'HH:mm');
    }

    if (allDatesAreSameYear) {
      return format(date, options.sameYearFormat ?? 'MMM d, HH:mm');
    }

    return format(date, 'MMM d, yyyy');
  };
};

const formatDateOrFallback = (
  value: string | number,
  fallbackFormatter?: (value: string) => string
): string => {
  const date = parseDateValue(value);

  if (!date) {
    return fallbackFormatter ? fallbackFormatter(String(value)) : String(value);
  }

  return format(date, 'MMM d, yyyy HH:mm');
};

export const formatAxisValue = (
  value: string | number,
  fallbackFormatter?: (value: string) => string
): string => {
  return formatDateOrFallback(value, fallbackFormatter);
};

export const formatTooltipValue = (
  value: string | number,
  fallbackFormatter?: (value: string) => string
): string => {
  return formatDateOrFallback(value, fallbackFormatter);
};
